import { useTranslation, Trans } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/common/components/dialog';

import {
  useRevokeApiKeyMutation,
  ApiKeyRow,
} from '@common-reducers/backend-api/backend-api';
import { namespace } from '@common/config/constants';

interface Props {
  row: ApiKeyRow | null;
  onOpenChange: (open: boolean) => void;
}

export function RevokeApiKeyDialog({ row, onOpenChange }: Props) {
  const { t } = useTranslation(namespace);
  const [revoke, { isLoading }] = useRevokeApiKeyMutation();

  const submit = async () => {
    if (!row) return;
    try {
      await revoke({ id: row._id }).unwrap();
      toast.success(t('api.keys.revokeDialog.success'));
      onOpenChange(false);
    } catch {
      toast.error(t('api.keys.revokeDialog.error'));
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('api.keys.revokeDialog.title')}</DialogTitle>
          <DialogDescription>
            <Trans
              t={t}
              i18nKey="api.keys.revokeDialog.body"
              values={{ name: row?.name ?? '' }}
              components={{ strong: <strong /> }}
            />
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={isLoading}
          >
            {t('api.keys.revokeDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
